import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Hash, Megaphone, Plus, Tag } from 'lucide-react';
import { Badge, Button, Input, ConfirmDialog } from '@/shared/ui';
import { formatPhoneDisplay } from '@/shared/utils/validation';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from '../pages/AgentsPage.module.css';
import { adsApi } from '@/modules/campaigns/api/adsApi';
import { adsKeys } from '@/modules/campaigns/queryKeys';
import { useCallSources, useCallSourceActions } from '../hooks/useCallSources';
import { SectionCard } from './builderShared';
import { useCanManageCalls } from '../hooks/useCanManageCalls';
import type { CallSource, SourceChannel } from '../api/callsApi';

import { CHANNEL_OPTIONS, channelLabel } from './channelLabel';

function sourceStatusBadge(status: CallSource['status']) {
  if (status === 'active') return <Badge variant="success">Live</Badge>;
  if (status === 'paused') return <Badge variant="warning">Paused</Badge>;
  return <Badge variant="neutral">Draft</Badge>;
}

// Sources & numbers — each source gets its own tracked number, provisioned at
// activation with the practice's approval (PRD §4.5).
export function SourcesSection({
  agentId,
  areaCode,
  open,
  onToggle,
}: {
  agentId: string | undefined;
  areaCode: string;
  open: boolean;
  onToggle: () => void;
}) {
  const { sources, locationId, isError: sourcesFailed } = useCallSources();
  const actions = useCallSourceActions(locationId);
  const canManage = useCanManageCalls();
  const [name, setName] = useState('');
  const [channel, setChannel] = useState<SourceChannel>('google_ads');
  const [campaignId, setCampaignId] = useState('');
  const [approveSourceId, setApproveSourceId] = useState<string | null>(null);
  const [deleteSourceId, setDeleteSourceId] = useState<string | null>(null);

  const { data: campaignsData } = useQuery({
    queryKey: adsKeys.campaigns(locationId, 'mtd'),
    queryFn: () => adsApi.getCampaigns(locationId),
    enabled: !!locationId,
  });
  const campaigns = campaignsData?.data ?? [];

  const agentSources = sources.filter((s) => s.agentId === agentId);
  const approveSource = agentSources.find((s) => s.id === approveSourceId);
  const deleteSource = agentSources.find((s) => s.id === deleteSourceId);

  const handleCreate = () => {
    if (!agentId || !name.trim()) return;
    actions.create.mutate(
      {
        locationId,
        agentId,
        name: name.trim(),
        channel,
        campaignId: campaignId || null,
      },
      { onSuccess: () => setName('') },
    );
  };

  return (
    <SectionCard
      icon={Megaphone}
      title="Sources & numbers"
      done={agentSources.length > 0}
      open={open}
      onToggle={onToggle}
    >
      <p className={styles.hintText}>
        Each source gets its own number. Put it on the ad, mailer, or landing page — every call is
        attributed automatically.
      </p>

      {sourcesFailed && (
        <div className={styles.warningRow}>
          <AlertTriangle size={12} /> Couldn't load this agent's sources — refresh the page to try
          again before adding new ones.
        </div>
      )}

      {!agentId && (
        <div className={styles.warningRow}>
          <AlertTriangle size={12} /> Save the draft first — sources attach to the saved agent.
        </div>
      )}

      {agentSources.length > 0 && (
        <div className={patterns.gridTable}>
          <div
            className={`${patterns.gridTableHeader} ${styles.sourceGridCols} ${styles.stackedHeader}`}
          >
            <span>Source</span>
            <span>Number</span>
            <span>Linked campaign</span>
            <span>Status</span>
            <span />
          </div>
          {agentSources.map((s) => (
            <div key={s.id} className={`${patterns.gridTableRow} ${styles.sourceGridCols}`}>
              <div>
                <div className={styles.cardTitle}>{s.name}</div>
                <div className={styles.agentCardType}>{channelLabel(s.channel)}</div>
              </div>
              <div className={patterns.cellSecondary}>
                {s.trackedNumber ? (
                  <span className={styles.metaRow}>
                    <Hash size={11} /> {formatPhoneDisplay(s.trackedNumber)}
                  </span>
                ) : (
                  'Pending — provisioned when you activate'
                )}
              </div>
              <div className={patterns.cellSecondary}>
                {s.campaignName ? (
                  <span className={styles.metaRow}>
                    <Tag size={11} /> {s.campaignName}
                  </span>
                ) : (
                  'Not linked'
                )}
              </div>
              <div>{sourceStatusBadge(s.status)}</div>
              <div className={styles.sourceActions}>
                {!canManage ? null : s.status === 'active' ? (
                  <Button variant="secondary" size="sm" onClick={() => actions.pause.mutate(s.id)}>
                    Pause
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => setApproveSourceId(s.id)}>
                    {s.trackedNumber ? 'Resume' : 'Activate'}
                  </Button>
                )}
                {canManage && s.status !== 'active' && (
                  <Button variant="secondary" size="sm" onClick={() => setDeleteSourceId(s.id)}>
                    Delete
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <div className={styles.addSourceBox}>
          <div className={styles.cardTitle}>
            <Plus size={13} className={styles.labelIcon} />
            Add a source
          </div>
          <div className={styles.fieldRow}>
            <div>
              <label className={styles.fieldLabel}>Source name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. GBP profile number"
                fullWidth
              />
            </div>
            <div>
              <label className={styles.fieldLabel}>Channel</label>
              <select
                className={styles.activateSelect}
                value={channel}
                onChange={(e) => setChannel(e.target.value as SourceChannel)}
              >
                {CHANNEL_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.fieldRow}>
            <div>
              <label className={styles.fieldLabel}>Link to campaign (optional)</label>
              <select
                className={styles.activateSelect}
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
              >
                <option value="">— none —</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.addSourceBtnCell}>
              <Button
                onClick={handleCreate}
                disabled={!agentId || !name.trim()}
                loading={actions.create.isPending}
                fullWidth
              >
                Create source
              </Button>
            </div>
          </div>
          <p className={styles.hintText}>
            Linking a campaign puts this source's calls, leads, and value on that campaign's ROI
            report in Ad Campaigns. The source's number is proposed in this agent's area code and
            confirmed with you before it goes live.
          </p>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteSource}
        onClose={() => setDeleteSourceId(null)}
        onConfirm={() => {
          if (!deleteSource) return;
          actions.remove.mutate(deleteSource.id, { onSuccess: () => setDeleteSourceId(null) });
        }}
        title="Delete source?"
        message={
          deleteSource?.trackedNumber
            ? `This removes "${deleteSource.name}" and its offer. Its number ${formatPhoneDisplay(deleteSource.trackedNumber)} is detached and stays on the Numbers page for reuse — anyone still calling it from an ad or mailer will reach nothing until you reassign or release it.`
            : `This removes "${deleteSource?.name ?? ''}" and its offer. It never had a number, so no calls are affected.`
        }
        confirmLabel="Delete source"
        variant="destructive"
        isLoading={actions.remove.isPending}
      />
      <ConfirmDialog
        isOpen={!!approveSource}
        onClose={() => setApproveSourceId(null)}
        onConfirm={() => {
          if (approveSourceId) {
            actions.activate.mutate(approveSourceId, {
              onSuccess: () => setApproveSourceId(null),
            });
          }
        }}
        title={approveSource?.trackedNumber ? 'Resume source' : 'Approve source number'}
        message={
          approveSource?.trackedNumber
            ? `Re-attach ${formatPhoneDisplay(approveSource.trackedNumber)} and resume attribution for "${approveSource.name}".`
            : `A tracked number in the ${areaCode} area code will be purchased for "${approveSource?.name ?? ''}" and answered by this agent. Nothing is set up until you approve.`
        }
        confirmLabel={approveSource?.trackedNumber ? 'Resume' : 'Approve & provision'}
        isLoading={actions.activate.isPending}
      />
    </SectionCard>
  );
}

// Offer details — what the agent is allowed to say, per source.
export function OfferSection({
  agentId,
  open,
  onToggle,
}: {
  agentId: string | undefined;
  open: boolean;
  onToggle: () => void;
}) {
  const { sources, locationId } = useCallSources();
  const actions = useCallSourceActions(locationId);
  const canManage = useCanManageCalls();
  const agentSources = sources.filter((s) => s.agentId === agentId);
  const [selectedId, setSelectedId] = useState<string>('');
  const selected = agentSources.find((s) => s.id === selectedId) ?? agentSources[0];
  const [draftOffer, setDraftOffer] = useState<{
    id: string;
    offerName: string;
    offerExpiry: string;
    offerTerms: string;
    targetService: string;
  } | null>(null);

  // Re-seed the local editor when the selected source changes
  const editing =
    draftOffer && draftOffer.id === selected?.id
      ? draftOffer
      : selected
        ? {
            id: selected.id,
            offerName: selected.offerName,
            offerExpiry: selected.offerExpiry,
            offerTerms: selected.offerTerms,
            targetService: selected.targetService,
          }
        : null;

  const setField = (field: keyof NonNullable<typeof editing>, value: string) => {
    if (!editing) return;
    setDraftOffer({ ...editing, [field]: value });
  };

  return (
    <SectionCard
      icon={Tag}
      title={`Offer details${selected ? ` — ${selected.name}` : ''}`}
      done={!!selected?.offerName}
      open={open}
      onToggle={onToggle}
    >
      {!selected ? (
        <p className={styles.hintText}>
          Add a source above — each source carries its own offer, and the agent fills in{' '}
          {'{{offer_name}}'} from whichever source's number was dialed.
        </p>
      ) : (
        <>
          <p className={styles.hintText}>
            What the agent is allowed to say about this offer. Each source has its own.
          </p>
          {agentSources.length > 1 && (
            <select
              className={styles.activateSelect}
              value={selected.id}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              {agentSources.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
          <div className={styles.fieldRow}>
            <div>
              <label className={styles.fieldLabel}>Offer name</label>
              <Input
                value={editing?.offerName ?? ''}
                onChange={(e) => setField('offerName', e.target.value)}
                placeholder="$99 new-patient exam + cleaning"
                fullWidth
              />
            </div>
            <div>
              <label className={styles.fieldLabel}>Expires</label>
              <Input
                value={editing?.offerExpiry ?? ''}
                onChange={(e) => setField('offerExpiry', e.target.value)}
                placeholder="September 30, 2026"
                fullWidth
              />
            </div>
          </div>
          <label className={styles.fieldLabel}>What's included & who qualifies</label>
          <textarea
            className={patterns.textarea}
            rows={3}
            value={editing?.offerTerms ?? ''}
            onChange={(e) => setField('offerTerms', e.target.value)}
            placeholder="Comprehensive exam, X-rays, and cleaning for $99 for new patients…"
          />
          <label className={styles.fieldLabel}>Target service (drives value estimation)</label>
          <Input
            value={editing?.targetService ?? ''}
            onChange={(e) => setField('targetService', e.target.value)}
            placeholder="e.g. Implant consult"
            fullWidth
          />
          <div className={styles.warningRow}>
            <AlertTriangle size={12} /> The agent only answers offer questions from what's written
            here. Anything else: "great question — the team will confirm that when they call you."
          </div>
          {canManage && (
            <Button
              size="sm"
              onClick={() =>
                editing &&
                actions.update.mutate({
                  id: editing.id,
                  data: {
                    offerName: editing.offerName,
                    offerExpiry: editing.offerExpiry,
                    offerTerms: editing.offerTerms,
                    targetService: editing.targetService,
                  },
                })
              }
              loading={actions.update.isPending}
            >
              Save offer
            </Button>
          )}
        </>
      )}
    </SectionCard>
  );
}
